import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';
import { BaseForm } from 'projects/admin/src/app/classes/base-form';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { ROWS_PER_PAGE } from 'projects/admin/src/app/shared/constants';
import { TemplateModel } from 'projects/core/src/lib/models/template.model';
import { DataScopeModel } from 'projects/core/src/lib/models/data-scope.model';

class CustomTemplateModel extends TemplateModel {
  control_enabled: UntypedFormControl = new UntypedFormControl(false); // tslint:disable-line: variable-name
}

@Component({
  selector: 'app-data-scope-templates',
  templateUrl: './data-scope-templates.component.html',
  styleUrls: ['./data-scope-templates.component.scss']
})
export class DataScopeTemplatesComponent extends BaseForm implements OnInit, OnChanges, OnDestroy {

  @Input() scope: DataScopeModel = null;
  @Output() setEdit = new EventEmitter<string>();
  @Output() entryCountChange = new EventEmitter<number>();

  public loading = false;
  public templates: Array<CustomTemplateModel> = [];
  public orphans: Array<{id: number, name: string}> = [];
  public rowsPerPage = ROWS_PER_PAGE;

  public editRight = 'admin.datascope.edit';

  public form = this.fb.group({});
  private initializedId: number = null;
  private countSubscription: Subscription = null;

  constructor(
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private api: ApiService,
    private transloco: TranslocoService,
    private auth: AuthStateService
  ) {
    super();
  }

  ngOnInit(): void {
    this.ui.setFocus();
  }

  ngOnDestroy(): void {
    if (this.countSubscription !== null) {
      this.countSubscription.unsubscribe();
    }
  }

  ngOnChanges(): void {
    this.initTemplates();
    this.setTemplates();
    this.setOrphans();
  }

  onSubmit(): void {
    this.ui.hideMessage();
    if (this.form.valid === true) {
      this.submitting = true;
      const payload: any = {
        ids: this.getTemplateIds()
      };
      this.api.saveDataScopeEntries(this.scope.id, 'template', payload).subscribe({
        next: (result: any) => {
          this.ui.toastSuccess(this.transloco.translate('common.messages.saved', { value: this.scope.name }), this.transloco.translate('common.saved'));
          this.scope.deserialize(result.data);
          this.stopEdit();
        },
        error: (error: any) => {
          this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
          FormHelper.setBackendErrors(this.form, error.fielderrors);
        }
      }).add(() => {
        this.submitting = false;
      });
    } else {
      this.ui.showMessage('error', this.transloco.translate('common.error_on_form_please_correct'), this.transloco.translate('common.error'));
      FormHelper.markFormAsDirty(this.form);
    }
  }

  hasEmptyEntries(): boolean {
    if (this.scope === null || this.scope.template === undefined) {
      return false;
    }
    if (this.inEdit === true) {
      return this.getTemplateIds().length === 0;
    }
    const entries = this.scope.template.entries;
    return !entries || entries.length === 0;
  }

  protected startEdit(): void {
    if (this.auth.hasRight(this.editRight)) {
      if (this.scope !== null) {
        this.form.patchValue({});
      }
      this.setEdit.emit('templates');
      super.startEdit();
      this.ui.setFormFocus();
    }
  }

  protected stopEdit(): void {
    this.form.reset();
    this.setEdit.emit(null);
    super.stopEdit();
    this.ui.setFocus();
    this.setTemplates();
    this.setOrphans();
  }

  private initTemplates(): void {
    if (this.scope === null || this.scope.template === undefined) {
      return;
    }
    if (this.initializedId === this.scope.id) {
      return;
    }
    this.initializedId = this.scope.id;
    this.form = this.fb.group({});
    this.templates = [];
    const selectable = this.scope.template.selectable || [];
    if (selectable.length > 0) {
      this.templates = selectable.map((template: any) => {
        const temp = new CustomTemplateModel().deserialize(template);
        this.form.addControl(temp.name + '_enabled', temp.control_enabled);
        return temp;
      }).sort((a: CustomTemplateModel, b: CustomTemplateModel) => {
        return (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0);
      });
    }
    this.watchEntryCount();
  }

  private setTemplates(): void {
    if (this.scope !== null && this.scope.template !== undefined) {
      this.templates.forEach((template: CustomTemplateModel) => {
        const found = this.scope.template.entries?.find((actual: string | number) => {
          return actual === template.id;
        });
        template.control_enabled.setValue(found !== undefined);
      });
    }
  }

  private setOrphans(): void {
    if (this.scope === null || this.scope.template === undefined) {
      this.orphans = [];
      return;
    }
    this.orphans = this.scope.template.orphans || [];
  }

  private watchEntryCount(): void {
    if (this.countSubscription !== null) {
      this.countSubscription.unsubscribe();
    }
    this.countSubscription = this.form.valueChanges.subscribe(() => {
      this.entryCountChange.emit(this.getTemplateIds().length);
    });
  }

  private getTemplateIds(): Array<number> {
    const ids: Array<number> = [];
    this.templates.forEach((template: CustomTemplateModel) => {
      if (template.control_enabled.value === true) {
        ids.push(template.id);
      }
    });
    this.orphans.forEach((orphan: {id: number, name: string}) => {
      ids.push(orphan.id);
    });
    return ids;
  }

}
